import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, money2, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Thumb, Pill } from "@/components/Bits";

export default function OptionSet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () =>
    api
      .get(`/option-sets/${id}/view`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const choose = async (scenarioId) => {
    setBusy(scenarioId);
    try {
      const { data: updated } = await api.post(`/client/option-sets/${id}/choose`, { scenario_id: scenarioId });
      setData(updated);
      toast.success("Thank you — your designer has been told");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <div className="py-24 text-center text-sm text-fog">Loading your options…</div>;

  const chosen = data.chosen_scenario_id;
  const locked = data.status === "Locked";

  return (
    <div className="space-y-10" data-testid="option-set-page">
      <button onClick={() => navigate("/decisions")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to your decisions
      </button>

      <PageHeader
        overline={data.space_name ? `${data.space_name} · Design Options` : "Design Options"}
        title={data.name}
        subtitle={data.note}
        testId="option-set-header"
      />

      {chosen && (
        <div className="card-surface flex flex-wrap items-center gap-4 p-6" data-testid="option-chosen-banner">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EDEFC4]">
            {locked ? <Lock className="h-4 w-4 text-lime-dark" strokeWidth={1.5} /> : <Check className="h-5 w-5 text-lime-dark" strokeWidth={1.5} />}
          </span>
          <div>
            <div className="font-serif text-xl text-ink">
              You chose {data.options.find((o) => o.scenario_id === chosen)?.label}
            </div>
            <div className="mt-1 text-xs text-fog">
              {dateShort(data.chosen_at)}
              {locked ? " · Your designer has locked this in" : " · You can still change your mind"}
            </div>
          </div>
        </div>
      )}

      <div className={`grid gap-7 ${data.options.length > 2 ? "xl:grid-cols-3" : "lg:grid-cols-2"}`} data-testid="option-grid">
        {data.options.map((o) => {
          const isChosen = chosen === o.scenario_id;
          return (
            <div
              key={o.scenario_id}
              data-testid={`option-card-${o.scenario_id}`}
              className={`card-surface flex flex-col p-7 transition-colors ${isChosen ? "border-lime bg-[#FCFCF7]" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-2xl leading-snug text-ink">{o.label}</h3>
                {isChosen && <Pill tone="bg-lime text-ink" testId={`option-chosen-${o.scenario_id}`}>Your choice</Pill>}
              </div>
              {o.note && <p className="mt-3 text-sm leading-relaxed text-stone">{o.note}</p>}

              <div className="mt-6 border-t border-line pt-5">
                <div className="overline">Option Total</div>
                <div className="num mt-2 text-3xl text-ink" data-testid={`option-total-${o.scenario_id}`}>{money(o.total)}</div>
                <div className="mt-1 text-xs text-fog">{o.item_count} pieces</div>
              </div>

              <div className="mt-6 space-y-4">
                {o.items.map((it, idx) => (
                  <div key={`${o.scenario_id}-${idx}`} className="flex items-center gap-4">
                    <Thumb src={it.image} alt={it.name} className="h-14 w-16 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-ink">{it.name}</div>
                      <div className="num mt-1 text-xs text-fog">
                        {money2(it.unit_price)}{it.quantity > 1 ? ` × ${it.quantity}` : ""}
                      </div>
                    </div>
                    <div className="num shrink-0 text-sm text-ink">{money2(it.line_total)}</div>
                  </div>
                ))}
              </div>

              {!isStaff && (
                <button
                  data-testid={`choose-option-${o.scenario_id}`}
                  disabled={busy === o.scenario_id || locked || isChosen}
                  onClick={() => choose(o.scenario_id)}
                  className={`mt-7 w-full rounded-sm px-5 py-3 text-sm font-medium transition-colors disabled:opacity-60 ${
                    isChosen ? "border border-line bg-white text-stone" : "bg-lime text-ink hover:bg-lime-dark"
                  }`}
                >
                  {isChosen ? "This is your choice" : busy === o.scenario_id ? "Saving…" : "Choose this option"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!isStaff && !locked && (
        <p className="text-xs text-fog">
          Nothing is ordered from this choice — your designer will confirm each piece with you as usual.
        </p>
      )}
    </div>
  );
}
